import { TimeSync } from './timeSync';

export interface PresenceUpdate {
  cardId: string;
  status: "viewing" | "editing";
  timestamp: number;
}

export class PresenceDebouncer {
  private pendingUpdates = new Map<string, PresenceUpdate>();
  private timeoutId: NodeJS.Timeout | null = null;
  private debounceTime: number;

  constructor(debounceTime: number = 100) {
    this.debounceTime = debounceTime;
  }

  addUpdate(cardId: string, status: "viewing" | "editing", callback: (updates: PresenceUpdate[]) => Promise<void>) {
    this.pendingUpdates.set(cardId, {
      cardId,
      status,
      timestamp: TimeSync.getServerTime()
    });

    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
    }

    // **PHASE 2: Immediate execution for editing status**
    if (status === "editing") {
      this.flush(callback);
      return;
    }

    this.timeoutId = setTimeout(() => {
      this.flush(callback);
    }, this.debounceTime);
  }

  private async flush(callback: (updates: PresenceUpdate[]) => Promise<void>) {
    if (this.pendingUpdates.size === 0) return;

    const updates = Array.from(this.pendingUpdates.values());
    this.pendingUpdates.clear();

    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
      this.timeoutId = null;
    }

    try {
      await callback(updates);
    } catch (error) {
      console.error('❌ Presence update failed:', error);
    }
  }

  clear() {
    this.pendingUpdates.clear();
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
      this.timeoutId = null;
    }
  }
}

export class ConnectionMonitor {
  private isHealthy = true;
  private connectionErrors = 0;
  private lastErrorTime = 0;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private baseReconnectDelay = 500;
  private healthCallbacks: Array<(isHealthy: boolean) => void> = [];

  onConnectionError() {
    this.connectionErrors++;
    this.lastErrorTime = Date.now();
    
    if (this.connectionErrors >= 3 && this.isHealthy) {
      this.isHealthy = false;
      this.notifyHealthChange();
    }
  }

  onReconnectSuccess() {
    if (!this.isHealthy || this.connectionErrors > 0) {
      this.isHealthy = true;
      this.connectionErrors = 0;
      this.reconnectAttempts = 0;
      this.notifyHealthChange();
    }
  }

  onReconnectAttempt() {
    this.reconnectAttempts++;
  }

  shouldReconnect(): boolean {
    return this.reconnectAttempts < this.maxReconnectAttempts;
  }

  getReconnectDelay(): number {
    const delay = Math.min(
      this.baseReconnectDelay * Math.pow(1.5, this.reconnectAttempts),
      3000
    );
    return delay;
  }

  isConnectionHealthy(): boolean {
    return this.isHealthy;
  }

  getStats() {
    return {
      isHealthy: this.isHealthy,
      connectionErrors: this.connectionErrors,
      reconnectAttempts: this.reconnectAttempts,
      timeSinceLastError: this.lastErrorTime ? Date.now() - this.lastErrorTime : null
    };
  }

  addHealthCallback(callback: (isHealthy: boolean) => void) {
    this.healthCallbacks.push(callback);
  }

  removeHealthCallback(callback: (isHealthy: boolean) => void) {
    const index = this.healthCallbacks.indexOf(callback);
    if (index > -1) {
      this.healthCallbacks.splice(index, 1);
    }
  }

  private notifyHealthChange() {
    this.healthCallbacks.forEach(callback => {
      try {
        callback(this.isHealthy);
      } catch (error) {
        console.error('❌ Health callback error:', error);
      }
    });
  }

  reset() {
    this.isHealthy = true;
    this.connectionErrors = 0;
    this.lastErrorTime = 0;
    this.reconnectAttempts = 0;
    this.notifyHealthChange();
  }
}

export class RealtimeUpdateBatcher {
  private updates: Map<string, any> = new Map();
  private processingTimeoutId: NodeJS.Timeout | null = null;
  private batchSize: number;
  private batchDelay: number;

  constructor(batchSize: number = 10, batchDelay: number = 50) {
    this.batchSize = batchSize;
    this.batchDelay = batchDelay;
  }

  addUpdate(key: string, update: any, processor: (updates: any[]) => void) {
    this.updates.set(key, update);

    if (this.updates.size >= this.batchSize) {
      this.processBatch(processor);
      return;
    }

    if (this.processingTimeoutId) {
      clearTimeout(this.processingTimeoutId);
    }

    this.processingTimeoutId = setTimeout(() => {
      this.processBatch(processor);
    }, this.batchDelay);
  }

  private processBatch(processor: (updates: any[]) => void) {
    if (this.updates.size === 0) return;

    const batchUpdates = Array.from(this.updates.values());
    this.updates.clear();

    if (this.processingTimeoutId) {
      clearTimeout(this.processingTimeoutId);
      this.processingTimeoutId = null;
    }

    try {
      processor(batchUpdates);
    } catch (error) {
      console.error('❌ Batch processing error:', error);
    }
  }

  clear() {
    this.updates.clear();
    if (this.processingTimeoutId) {
      clearTimeout(this.processingTimeoutId);
      this.processingTimeoutId = null;
    }
  }
}

export class PresencePerformanceMonitor {
  private metrics = {
    updateLatency: [] as number[],
    batchSizes: [] as number[],
    errorRate: 0,
    totalUpdates: 0,
    totalErrors: 0
  };

  recordUpdateLatency(startTime: number, endTime: number) {
    const latency = endTime - startTime;
    this.metrics.updateLatency.push(latency);
    
    if (this.metrics.updateLatency.length > 100) {
      this.metrics.updateLatency.shift();
    }
  }

  recordBatchSize(size: number) {
    this.metrics.batchSizes.push(size);
    this.metrics.totalUpdates += size;
    
    if (this.metrics.batchSizes.length > 100) {
      this.metrics.batchSizes.shift();
    }
  }

  recordError() {
    this.metrics.totalErrors++;
    this.metrics.errorRate = this.metrics.totalErrors / Math.max(this.metrics.totalUpdates, 1);
  }

  getAverageLatency(): number {
    if (this.metrics.updateLatency.length === 0) return 0;
    return this.metrics.updateLatency.reduce((a, b) => a + b, 0) / this.metrics.updateLatency.length;
  }

  getAverageBatchSize(): number {
    if (this.metrics.batchSizes.length === 0) return 0;
    return this.metrics.batchSizes.reduce((a, b) => a + b, 0) / this.metrics.batchSizes.length;
  }

  getMetrics() {
    return {
      ...this.metrics,
      averageLatency: this.getAverageLatency(),
      averageBatchSize: this.getAverageBatchSize()
    };
  }

  reset() {
    this.metrics = {
      updateLatency: [],
      batchSizes: [],
      errorRate: 0,
      totalUpdates: 0,
      totalErrors: 0
    };
  }
}

export class PresenceCache {
  private cache = new Map<string, { data: any; timestamp: number; ttl: number }>();
  private defaultTTL: number;

  constructor(defaultTTL: number = 2000) {
    this.defaultTTL = defaultTTL;
  }

  set(key: string, data: any, ttl?: number): void {
    this.cache.set(key, {
      data,
      timestamp: TimeSync.getServerTime(),
      ttl: ttl ?? this.defaultTTL
    });
  }

  get(key: string): any | null {
    const entry = this.cache.get(key);
    if (!entry) return null;

    const age = TimeSync.getServerTime() - entry.timestamp;
    if (age > entry.ttl) {
      this.cache.delete(key);
      return null;
    }

    return entry.data;
  }

  has(key: string): boolean {
    return this.get(key) !== null;
  }

  invalidate(key: string): void {
    this.cache.delete(key);
  }

  clear(): void {
    this.cache.clear();
  }

  cleanup(): void {
    const now = TimeSync.getServerTime();
    for (const [key, entry] of this.cache.entries()) {
      const age = now - entry.timestamp;
      if (age > entry.ttl) {
        this.cache.delete(key);
      }
    }
  }

  size(): number {
    this.cleanup();
    return this.cache.size;
  }
}
